import React from 'react';
import PropTypes from 'prop-types';
import { ActionButtons } from './sectionShared';

const getCsatScoreClass = (value) => {
  const score = Number(value);
  if (!Number.isFinite(score)) return '';
  if (score > 4) return 'score-green';
  if (score > 3) return 'score-amber';
  return 'score-red';
};

const calculateCsatGauge = (score, maxScore = 5) => {
  const percentage = (score / maxScore) * 100;
  const arcLength = 220;
  const filledLength = (percentage / 100) * arcLength;
  return arcLength - filledLength;
};

const CsatSummaryCards = ({
  csatOverallTotal,
  csatOverallAverage,
  csatData,
  csatCurrentYearTotal,
  csatCurrentYearAverage,
  csatScore,
  csatDate,
  csatLastReceived,
}) => (
  <div className="summary-row display-none">
    <div className="summary-card">
      <div className="summary-header">Overall</div>
      <div className="summary-body">
        <div className="summary-metric">
          <div className="metric-icon blue">&Sigma;</div>
          <div className="metric-text">
            <span>Total no. of CSAT received</span>
            <strong>{csatOverallTotal}</strong>
          </div>
        </div>
        <div className="metric-divider" />
        <div className="summary-metric">
          <div className="metric-icon blue">~</div>
          <div className="metric-text">
            <span>Average</span>
            <strong>{csatOverallAverage.toFixed(1)}</strong>
          </div>
        </div>
      </div>
    </div>

    <div className="summary-card">
      <div className="summary-header">Current financial year</div>
      <div className="summary-subheader">{csatData?.currentYear?.year || '-'}</div>
      <div className="summary-body">
        <div className="summary-metric">
          <div className="metric-icon blue">&Sigma;</div>
          <div className="metric-text">
            <span>Total no. of CSAT received</span>
            <strong>{csatCurrentYearTotal}</strong>
          </div>
        </div>
        <div className="metric-divider" />
        <div className="summary-metric">
          <div className="metric-icon blue">~</div>
          <div className="metric-text">
            <span>Average</span>
            <strong>{csatCurrentYearAverage.toFixed(1)}</strong>
          </div>
        </div>
      </div>
    </div>

    <div className="summary-card">
      <div className="summary-header">Latest CSAT</div>
      <div className="summary-body latest-csat">
        <div className="csat-gauge-small">
          <svg viewBox="0 0 200 120">
            <path
              d="M 30 100 A 70 70 0 0 1 170 100"
              fill="none"
              stroke="#F1F5F8"
              strokeWidth="20"
              strokeLinecap="round"
            />
            <path
              d="M 30 100 A 70 70 0 0 1 170 100"
              fill="none"
              stroke="#DBDEFF"
              strokeWidth="20"
              strokeLinecap="round"
              strokeDasharray="220 220"
              strokeDashoffset={calculateCsatGauge(csatScore)}
            />
            <text x="100" y="85" textAnchor="middle" className="gauge-score">
              {csatScore.toFixed(2)}
            </text>
            <text x="100" y="105" textAnchor="middle" className="gauge-date">
              {csatDate}
            </text>
          </svg>
        </div>
        <div className="metric-divider" />
        <div className="summary-metric">
          <div className="metric-icon blue">&#9201;</div>
          <div className="metric-text">
            <span>Last CSAT received</span>
            <strong>{csatLastReceived}</strong>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const CsatTable = ({
  getSortClass,
  csatSort,
  getAriaSort,
  handleCsatSort,
  csatTableData,
  projectName,
  resolveCsatCustomerName,
  showActions,
  canEditCsat,
  canDeleteCsat,
  handleCSATEdit,
  handleCSATDelete,
  isCsatEmpty,
}) => (
  <div className="csat-table-wrapper">
    <div className="csat-table-scroll">
      <table className="csat-data-table">
        <thead>
          <tr>
            <th className="th-cell">
              <div className="th-content">
                <span>Project Name</span>
              </div>
            </th>
            <th className="th-cell">
              <div className="th-content">
                <span>CSAT Received Date</span>
              </div>
            </th>
            <th
              className={`th-cell sortable ${getSortClass('serviceQuality')}`}
              aria-sort={getAriaSort(csatSort.key, 'serviceQuality', csatSort.direction)}
            >
              <button
                type="button"
                className="th-content"
                onClick={() => handleCsatSort('serviceQuality')}
              >
                <span>Niveus service quality</span>
                <span className="sort-icon" />
              </button>
            </th>
            <th
              className={`th-cell sortable ${getSortClass('roi')}`}
              aria-sort={getAriaSort(csatSort.key, 'roi', csatSort.direction)}
            >
              <button type="button" className="th-content" onClick={() => handleCsatSort('roi')}>
                <span>ROI on your project</span>
                <span className="sort-icon" />
              </button>
            </th>
            <th
              className={`th-cell sortable ${getSortClass('management')}`}
              aria-sort={getAriaSort(csatSort.key, 'management', csatSort.direction)}
            >
              <button
                type="button"
                className="th-content"
                onClick={() => handleCsatSort('management')}
              >
                <span>Proj. mgmt. &amp; communication</span>
                <span className="sort-icon" />
              </button>
            </th>
            <th
              className={`th-cell sortable ${getSortClass('overall')}`}
              aria-sort={getAriaSort(csatSort.key, 'overall', csatSort.direction)}
            >
              <button
                type="button"
                className="th-content"
                onClick={() => handleCsatSort('overall')}
              >
                <span>Overall experience</span>
                <span className="sort-icon" />
              </button>
            </th>
            <th
              className={`th-cell sortable ${getSortClass('customer')}`}
              aria-sort={getAriaSort(csatSort.key, 'customer', csatSort.direction)}
            >
              <button
                type="button"
                className="th-content"
                onClick={() => handleCsatSort('customer')}
              >
                <span>Customer Name</span>
                <span className="sort-icon" />
              </button>
            </th>
            <th
              className={`th-cell sortable ${getSortClass('feedback')}`}
              aria-sort={getAriaSort(csatSort.key, 'feedback', csatSort.direction)}
            >
              <button
                type="button"
                className="th-content"
                onClick={() => handleCsatSort('feedback')}
              >
                <span>Other Feedback</span>
                <span className="sort-icon" />
              </button>
            </th>
            {showActions ? (
              <th className="th-cell th-actions">
                <div className="th-content th-actions-content">
                  <span>Actions</span>
                </div>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {csatTableData.map((row, idx) => (
            <tr key={row.id || idx} className="tr-data">
              <td className="td-cell">{projectName || '-'}</td>
              <td className="td-cell">{row.receivedDate || '-'}</td>
              <td className={`td-cell ${getCsatScoreClass(row.serviceQuality)}`}>
                {Number(row.serviceQuality || 0).toFixed(2)}
              </td>
              <td className={`td-cell ${getCsatScoreClass(row.roi)}`}>
                {Number(row.roi || 0).toFixed(2)}
              </td>
              <td className={`td-cell ${getCsatScoreClass(row.management)}`}>
                {Number(row.management || 0).toFixed(2)}
              </td>
              <td className={`td-cell ${getCsatScoreClass(row.overall)}`}>
                {Number(row.overall || 0).toFixed(2)}
              </td>
              <td className="td-cell td-customer">{resolveCsatCustomerName(row)}</td>
              <td className="td-cell td-feedback" data-feedback={row.feedback || ''}>
                <span className="feedback-text">{row.feedback || ''}</span>
                {row.feedback ? <span className="feedback-tooltip">{row.feedback}</span> : null}
              </td>
              {showActions ? (
                <td className="td-cell td-actions">
                  <ActionButtons
                    onEdit={canEditCsat ? () => handleCSATEdit(row) : null}
                    onDelete={canDeleteCsat ? () => handleCSATDelete(row) : null}
                  />
                </td>
              ) : null}
            </tr>
          ))}
          {isCsatEmpty ? (
            <tr className="tr-data tr-empty">
              <td className="td-cell td-empty" colSpan={showActions ? 9 : 8}>No CSAT records</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  </div>
);

CsatSummaryCards.propTypes = {
  csatOverallTotal: PropTypes.number.isRequired,
  csatOverallAverage: PropTypes.number.isRequired,
  csatData: PropTypes.object.isRequired,
  csatCurrentYearTotal: PropTypes.number.isRequired,
  csatCurrentYearAverage: PropTypes.number.isRequired,
  csatScore: PropTypes.number.isRequired,
  csatDate: PropTypes.string.isRequired,
  csatLastReceived: PropTypes.string.isRequired,
};

CsatTable.propTypes = {
  getSortClass: PropTypes.func.isRequired,
  csatSort: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.string,
  }).isRequired,
  getAriaSort: PropTypes.func.isRequired,
  handleCsatSort: PropTypes.func.isRequired,
  csatTableData: PropTypes.array.isRequired,
  projectName: PropTypes.string,
  resolveCsatCustomerName: PropTypes.func.isRequired,
  showActions: PropTypes.bool,
  canEditCsat: PropTypes.bool,
  canDeleteCsat: PropTypes.bool,
  handleCSATEdit: PropTypes.func.isRequired,
  handleCSATDelete: PropTypes.func.isRequired,
  isCsatEmpty: PropTypes.bool.isRequired,
};

CsatTable.defaultProps = {
  showActions: true,
  canEditCsat: true,
  canDeleteCsat: true,
  projectName: '',
};

export { CsatSummaryCards, CsatTable };
